import { useFailureAnalytics } from '@/hooks/useAdmin';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { StatsCard } from '@/components/admin/StatsCard';

const formatMinutes = (mins: number): string => {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const MetricsTable = ({
  title,
  data,
}: {
  title: string;
  data: Readonly<Record<string, number>>;
}) => {
  const entries = Object.entries(data).sort(([, a], [, b]) => b - a);
  if (entries.length === 0) return null;

  const maxCount = Math.max(...entries.map(([, v]) => v), 1);

  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-default)',
        backgroundColor: 'var(--bg-surface)',
        padding: 'var(--space-5)',
      }}
    >
      <h3
        style={{
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--text-tertiary)',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          marginBottom: 'var(--space-4)',
        }}
      >
        {title}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {entries.map(([key, count]) => {
          const pct = (count / maxCount) * 100;
          return (
            <div key={key}>
              <div className="flex items-center justify-between" style={{ marginBottom: '4px' }}>
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-secondary)',
                    textTransform: 'capitalize',
                  }}
                >
                  {key.replace(/_/g, ' ')}
                </span>
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--text-primary)',
                    fontFamily: 'var(--font-mono, monospace)',
                  }}
                >
                  {count}
                </span>
              </div>
              <div
                style={{
                  height: '4px',
                  borderRadius: '2px',
                  backgroundColor: 'var(--bg-surface-sunken)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${pct}%`,
                    borderRadius: '2px',
                    backgroundColor: 'var(--color-primary-400)',
                    transition: 'width var(--duration-normal) var(--ease-out)',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const AdminAnalyticsPage = () => {
  const { data: analytics, isLoading } = useFailureAnalytics();

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ color: 'var(--text-tertiary)', padding: 'var(--space-8)' }}>
        Failed to load analytics data.
      </div>
    );
  }

  const noData =
    analytics.totalFailures === 0 &&
    analytics.totalReprints === 0 &&
    Object.keys(analytics.failuresByCategory).length === 0;

  return (
    <div>
      <h1
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--text-primary)',
          letterSpacing: 'var(--tracking-tight)',
          marginBottom: 'var(--space-6)',
        }}
      >
        Failure Analytics
      </h1>

      {noData ? (
        <div
          style={{
            padding: 'var(--space-12)',
            textAlign: 'center',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-default)',
            backgroundColor: 'var(--bg-surface)',
          }}
        >
          <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
            No failure data recorded yet. This is a good sign.
          </p>
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div
            className="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: 'var(--space-3)',
              marginBottom: 'var(--space-6)',
            }}
          >
            <StatsCard label="Total Failures" value={String(analytics.totalFailures)} accent="danger" />
            <StatsCard label="Total Reprints" value={String(analytics.totalReprints)} accent="warning" />
            <StatsCard label="Failure Rate" value={`${analytics.failureRate}%`} accent="danger" />
            <StatsCard
              label="Avg Reprints/Failure"
              value={String(analytics.avgReprintsPerFailure)}
            />
            <StatsCard
              label="Print Time Lost"
              value={formatMinutes(analytics.totalPrintTimeMinutesLost)}
              accent="warning"
            />
          </div>

          {/* Breakdown tables with visual bars */}
          <div
            className="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
              gap: 'var(--space-4)',
            }}
          >
            <MetricsTable title="Failures by Category" data={analytics.failuresByCategory} />
            <MetricsTable title="Failures by Material" data={analytics.failuresByMaterial} />
            <MetricsTable title="Failures by Color" data={analytics.failuresByColor} />
            <MetricsTable title="Rejection Reasons" data={analytics.rejectionReasons} />
          </div>
        </>
      )}
    </div>
  );
};
